import { canonicalId, type RouteKey } from './routeKey'

/**
 * What the app knew about the route at the moment it was recorded.
 *
 * `computedAtIso` is WRITE time, not routing-computation time. `Route`
 * carries no timestamp on any platform, so iOS stamps the write and the
 * contract (§2.3) fixes that meaning for Android and Web too — a platform
 * that "fixed" this by stamping its own computation time would give one
 * field two meanings. Renders of a duration must respect the 24 h
 * freshness TTL rather than trust the number blindly.
 *
 * Deliberately stores no station names and no localized strings: names are
 * resolved from the schema at render time, so a language switch cannot
 * leave a stale English row behind.
 */
export interface RouteSnapshot {
  readonly durationMinutes: number
  readonly changesCount: number
  readonly lineIds: readonly string[]
  readonly computedAtIso: string
}

export interface RouteHistoryEntry {
  readonly key: RouteKey
  readonly snapshot: RouteSnapshot | null
  readonly lastUsedIso: string
  /**
   * How many times this route has been opened (§2.2). Never rendered as a raw
   * number — it exists to rank entries for suggestions and predictions.
   *
   * Owned by {@link recordEntry}, which recomputes it from the stored row and
   * ignores whatever a caller passes in. iOS enforces the same invariant by
   * construction (its `RouteHistoryList.recording` takes no count at all).
   */
  readonly useCount: number
}

/**
 * Contract cap (§2.2). The same number on iOS and Android — deliberately
 * larger than the station-recents cap, because a person has more distinct
 * routes than distinct interesting stations.
 */
export const HISTORY_CAP = 20

/**
 * Most-recent-first, one row per key. A repeat use moves the existing row to
 * the front; it never appends a duplicate. Mirrors iOS
 * `RouteHistoryList.recording` field for field.
 *
 * Three invariants that are easy to get wrong, all of them contract §2.2:
 *
 * - **`useCount` is recomputed here**, from the stored row — `entry.useCount`
 *   is deliberately ignored, so no call site can desync the count.
 * - **A `null` incoming snapshot preserves the stored one.** "We could not
 *   compute one this time" must not erase a usable older frame.
 * - **Eviction is by recency, not frequency.** A route not opened in months
 *   falls out even if it was once the most frequent; ranking by count here
 *   would turn the cap into a hall of fame that recent routes cannot enter.
 *
 * Pure: returns a new array, never mutates `list`.
 */
export function recordEntry(
  list: readonly RouteHistoryEntry[],
  entry: RouteHistoryEntry,
): RouteHistoryEntry[] {
  const id = canonicalId(entry.key)
  const existing = list.find((row) => canonicalId(row.key) === id)
  const recorded: RouteHistoryEntry = {
    key: entry.key,
    snapshot: entry.snapshot ?? existing?.snapshot ?? null,
    lastUsedIso: entry.lastUsedIso,
    useCount: (existing?.useCount ?? 0) + 1,
  }
  const withoutDuplicate = list.filter((row) => canonicalId(row.key) !== id)
  return [recorded, ...withoutDuplicate].slice(0, HISTORY_CAP)
}
